import axiosInstance from '../auth/axiosInstance';

export type ReportEditChartOption = {
  chart_id: string;
  label: string;
  description?: string;
  height: number;
  tags?: string[];
};

export type ReportEditSlot = {
  slot: string;
  chart_id: string;
  page: number;
  page_heading?: string;
  printed: string;
};

export type ReportEditPage = {
  page: number;
  heading?: string;
  printed: string;
  slots?: string[];
  free_space: number;
  overflowing?: boolean;
  custom: boolean;
};

export type ReportEditLayout = {
  layout: string;
  label: string;
  needs: string[];
  arrangement?: string;
};

export type ReportEditOptionsResponse = {
  charts: ReportEditChartOption[];
  slots: ReportEditSlot[];
  pages: ReportEditPage[];
  measured?: boolean;
  layouts: ReportEditLayout[];
};

export type ReportEditSkippedItem = {
  reason: string;
  operation: Record<string, unknown>;
};

export type ReportEditApplyResponse = {
  html: string;
  applied: Record<string, unknown>[];
  skipped: ReportEditSkippedItem[];
  slots?: ReportEditSlot[];
  page_count?: number;
  dry_run?: boolean;
};

export type ReportEditOperation = Record<string, unknown>;

export type ChartPreviewResponse = {
  chart_id: string;
  label: string;
  height: number;
  svg: string;
};

export async function previewReportChart(params: {
  chartId: string;
  payload: unknown;
  html?: string;
}): Promise<ChartPreviewResponse> {
  try {
    const { data } = await axiosInstance.post<ChartPreviewResponse>(
      '/Report/report-edit/chart-preview',
      {
        chartId: params.chartId,
        payload: params.payload,
        html: params.html ?? null,
      }
    );
    return data;
  } catch (error: unknown) {
    const message = await extractApiErrorMessage(error);
    throw new Error(message);
  }
}

async function extractApiErrorMessage(error: unknown): Promise<string> {
  const ax = error as {
    response?: { data?: unknown; status?: number };
    message?: string;
  };
  const status = ax.response?.status;
  const data = ax.response?.data;

  if (typeof data === 'object' && data !== null && 'message' in data) {
    const raw = String((data as { message?: string }).message || '');
    if (raw.includes('404') || status === 404) {
      return 'That chart is not available for preview.';
    }
    if (raw.includes('422') || status === 422) {
      return 'This report data cannot draw that chart.';
    }
    return raw || 'Chart preview failed.';
  }

  if (status === 404) {
    return 'That chart is not available for preview.';
  }
  if (status === 422) {
    return 'This report data cannot draw that chart.';
  }

  return ax.message || 'Chart preview failed.';
}

export async function fetchReportSourceHtml(
  projectId: number,
  reportItemId: string
): Promise<string> {
  const { data } = await axiosInstance.get<string>(
    `/Report/${projectId}/reports/${encodeURIComponent(reportItemId)}/source-html`,
    { responseType: 'text' }
  );
  return typeof data === 'string' ? data : String(data ?? '');
}

/** Same assessment payload used for generate, rebuilt for the project at call time. */
export async function fetchReportEditPayload(projectId: number): Promise<unknown> {
  const { data } = await axiosInstance.get(`/Report/${projectId}/report-edit-payload`);
  return data;
}

export async function fetchReportEditOptions(params: {
  projectId: number;
  reportItemId?: string;
  /** In-memory HTML after prior edits; omit to load from stored source HTML. */
  html?: string;
  /** Frozen session payload — avoids rebuilding on every options call. */
  payload?: unknown;
}): Promise<ReportEditOptionsResponse> {
  const { data } = await axiosInstance.post<ReportEditOptionsResponse>(
    '/Report/report-edit/options',
    {
      projectId: params.projectId,
      reportItemId: params.reportItemId ?? null,
      html: params.html ?? null,
      payload: params.payload ?? null,
    }
  );
  return data;
}

export async function stageReportEditHtml(
  projectId: number,
  html: string
): Promise<string> {
  const { data } = await axiosInstance.post<{ sourceHtmlPath?: string }>(
    '/Report/report-edit/stage-html',
    { projectId, html }
  );
  const path = data?.sourceHtmlPath?.trim();
  if (!path) {
    throw new Error('Server did not return a staged HTML path.');
  }
  return path;
}

export async function renderReportEditPdf(html: string): Promise<Blob> {
  const form = new FormData();
  form.append('html', html);
  const response = await axiosInstance.post('/Report/report-edit/render-pdf', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    responseType: 'blob',
    timeout: 0,
  });

  const contentType = response.headers['content-type'] || '';
  if (contentType.includes('application/json') || contentType.includes('text/plain')) {
    const text =
      response.data instanceof Blob ? await response.data.text() : String(response.data);
    let message = text;
    try {
      const parsed = JSON.parse(text);
      message = parsed?.message || parsed?.title || text;
    } catch {
      // keep text
    }
    throw new Error(message || 'Failed to render PDF.');
  }

  return response.data;
}

export async function applyReportEdit(params: {
  projectId: number;
  reportItemId?: string;
  /** Current working HTML; omit if sending `report` file or loading from reportItemId. */
  html?: string;
  payload: unknown;
  operations: ReportEditOperation[];
  dryRun?: boolean;
  files?: File[];
}): Promise<ReportEditApplyResponse> {
  const form = new FormData();
  form.append('projectId', String(params.projectId));
  if (params.reportItemId) {
    form.append('reportItemId', params.reportItemId);
  }
  if (params.html) {
    form.append('html', params.html);
  }
  form.append('payload', JSON.stringify(params.payload));
  form.append('operations', JSON.stringify(params.operations));
  if (params.dryRun) {
    form.append('dryRun', 'true');
  }
  if (params.files?.length) {
    for (const file of params.files) {
      form.append('files', file, file.name);
    }
  }

  const { data } = await axiosInstance.post<ReportEditApplyResponse>(
    '/Report/report-edit',
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0,
    }
  );
  return data;
}

/** options → edit → replace working html → options again */
export async function runReportEditStep(params: {
  projectId: number;
  reportItemId?: string;
  html: string;
  payload: unknown;
  operations: ReportEditOperation[];
  dryRun?: boolean;
  files?: File[];
}): Promise<{
  apply: ReportEditApplyResponse;
  options: ReportEditOptionsResponse;
  html: string;
}> {
  const apply = await applyReportEdit({
    projectId: params.projectId,
    reportItemId: params.reportItemId,
    html: params.html,
    payload: params.payload,
    operations: params.operations,
    dryRun: params.dryRun,
    files: params.files,
  });

  const nextHtml = params.dryRun ? params.html : apply.html;
  const options = await fetchReportEditOptions({
    projectId: params.projectId,
    reportItemId: params.reportItemId,
    html: nextHtml,
    payload: params.payload,
  });

  return { apply, options, html: nextHtml };
}
